import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import {
  Box,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Fade,
  Skeleton,
  TextField,
  Autocomplete,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetIcon,
  TrendingUp as TrendingUpIcon,
  Public as PublicIcon,
  Inventory as InventoryIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarMonth as CalendarIcon,
  BusinessCenter as BusinessCenterIcon,
} from '@mui/icons-material';
import { useGetCensusTradeDataQuery, useSearchHtsCodesQuery, useCalculateDutyRatesForCountriesQuery, useLazyCalculateDutyRatesForCountriesQuery, useGetAverageUnitCostQuery } from '@/services';
import { useGetHTSCodesQuery } from '@/services/dashboard/sm-api';
import { useAppSelector } from '@/store';
import { numberWithCommas, formatHTSCode } from '@/utils';
import { roles } from '@/roles';
import { HTSCodeWithTooltip } from '@/components';
import { tokens } from '@/tokens';

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Year options for the dropdown
const YEAR_OPTIONS = [
  { value: 'all', label: 'All Years (2022-Present)' },
  { value: '2024', label: '2024' },
  { value: '2023', label: '2023' },
  { value: '2022', label: '2022' },
];

// Country name mapping for data matching
const COUNTRY_NAME_MAP: Record<string, string> = {
  "United States of America": "United States",
  "USA": "United States",
  "Republic of Korea": "Korea, South",
  "South Korea": "Korea, South",
  "Korea": "Korea, South",
  "Democratic Republic of the Congo": "Congo (Kinshasa)",
  "Republic of the Congo": "Congo (Brazzaville)",
  "Czechia": "Czech Republic",
  "North Macedonia": "Macedonia",
  "Myanmar": "Burma",
  "Taiwan": "Taiwan",
  "Côte d'Ivoire": "Cote d'Ivoire",
  "Russian Federation": "Russia",
  "Viet Nam": "Vietnam",
  "United Kingdom": "United Kingdom",
  "UAE": "United Arab Emirates",
};

// Approximate longitude for major trading countries (used for popup positioning)
// Positive = East (Asia, Europe right side), Negative = West (Americas)
const COUNTRY_LONGITUDE: Record<string, number> = {
  "China": 105, "Japan": 138, "Korea, South": 127, "Taiwan": 121, "Vietnam": 106,
  "India": 79, "Thailand": 101, "Malaysia": 102, "Indonesia": 118, "Philippines": 122,
  "Germany": 10, "United Kingdom": -1, "France": 2, "Italy": 12, "Netherlands": 5,
  "Spain": -4, "Belgium": 4, "Switzerland": 8, "Poland": 19, "Ireland": -8,
  "Canada": -106, "Mexico": -102, "Brazil": -53, "Colombia": -74, "Chile": -71,
  "United States": -98, "Russia": 100, "Australia": 134, "New Zealand": 174,
  "South Africa": 25, "Nigeria": 8, "Egypt": 30, "Israel": 35, "Turkey": 35,
  "Saudi Arabia": 45, "United Arab Emirates": 54, "Singapore": 104, "Hong Kong": 114,
};

// Get country longitude (default to 10/slightly east if not found - popup will appear on left)
const getCountryLongitude = (countryName: string): number => {
  // Check direct match first
  if (COUNTRY_LONGITUDE[countryName] !== undefined) return COUNTRY_LONGITUDE[countryName];
  // Check mapped name
  const mappedName = COUNTRY_NAME_MAP[countryName];
  if (mappedName && COUNTRY_LONGITUDE[mappedName] !== undefined) return COUNTRY_LONGITUDE[mappedName];
  // Default to slightly east (popup will appear on left)
  return 10;
};

interface CountryData {
  country: string;
  totalValue: number;
  percentage: number;
  totalQuantity?: number | null;
  unit?: string | null;
  unitPrice?: number | null;
}

interface HtsOption {
  hts_code: string;
  description?: string;
}

interface ClientHtsOption {
  hts_code: string;
  goods_value: number;
  duty_value?: number;
  formatted_hts_code: string;
}

// Union type for all HTS options
type AnyHtsOption = HtsOption | ClientHtsOption;

// Type guard for client HTS codes
const isClientHtsOption = (option: AnyHtsOption): option is ClientHtsOption => {
  return 'goods_value' in option;
};

// Normalize HTS code for comparison (strip dots and leading zeros)
const normalizeHtsCode = (code: string): string => {
  const normalized = code.replace(/\./g, '').replace(/^0+/, '');
  // Return original (without dots) if stripping zeros results in empty string
  // This prevents "0000" from matching everything via String.includes("")
  return normalized || code.replace(/\./g, '');
};

const CensusDataView: React.FC = () => {
  const [position, setPosition] = useState({ coordinates: [10, 20] as [number, number], zoom: 1 });
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  
  // Search state
  const [htsSearchValue, setHtsSearchValue] = useState<HtsOption | string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchMode, setSearchMode] = useState<'search' | 'your_codes'>('your_codes');
  
  // Year filter state
  const [selectedYear, setSelectedYear] = useState<string>('all');

  // Get client context for "Your Codes" feature
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  // Determine clientId based on user role
  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const clientId = useMemo(() => {
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    // For client roles, use their own user id (backend expects client/user ObjectId)
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?._id;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  // Fetch client's HTS codes for "Your Codes" mode
  const { data: clientHtsCodes, isLoading: isLoadingClientCodes } = useGetHTSCodesQuery(
    { clientId: clientId || "" },
    { skip: !clientId }
  );

  // Process client HTS codes for dropdown
  const sortedClientHtsCodes: ClientHtsOption[] = useMemo(() => {
    if (!clientHtsCodes?.getHTSCodesByCountry) return [];
    
    return [...clientHtsCodes.getHTSCodesByCountry]
      .sort((a, b) => b.goods_value - a.goods_value)
      .map(code => ({
        ...code,
        formatted_hts_code: formatHTSCode(code.hts_code)
      }));
  }, [clientHtsCodes]);

  // Debounce search term for API
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Filter client codes based on search term
  const filteredClientCodes = useMemo(() => {
    if (!searchTerm) return sortedClientHtsCodes;
    const normalizedSearch = normalizeHtsCode(searchTerm);
    return sortedClientHtsCodes.filter(code => 
      normalizeHtsCode(code.hts_code).includes(normalizedSearch) ||
      code.formatted_hts_code.includes(searchTerm)
    );
  }, [sortedClientHtsCodes, searchTerm]);

  // Fetch HTS suggestions for search mode
  const { data: searchResults, isLoading: isSearchLoading, isFetching: isSearchFetching } = useSearchHtsCodesQuery(
    { query: debouncedSearchTerm, limit: 15 },
    { skip: debouncedSearchTerm.length < 2 || searchMode !== 'search' }
  );

  // Extract HTS code from selection (for display)
  const selectedHtsCode = typeof htsSearchValue === 'object' && htsSearchValue 
    ? htsSearchValue.hts_code 
    : (typeof htsSearchValue === 'string' ? htsSearchValue : undefined);

  // Strip dots from HTS code for API calls
  const selectedHtsCodeForApi = selectedHtsCode?.replace(/\./g, '');

  const selectedDescription = typeof htsSearchValue === 'object' && htsSearchValue 
    ? htsSearchValue.description 
    : undefined;

  // Convert year to number for API (null = all years)
  const yearParam = selectedYear === 'all' ? undefined : parseInt(selectedYear, 10);

  const { data, isLoading, isFetching } = useGetCensusTradeDataQuery({
    htsCode: selectedHtsCodeForApi,
    year: yearParam,
    limit: 300
  });

  // Fetch average unit cost from importer's ACE data
  const { data: averageUnitCostData } = useGetAverageUnitCostQuery({
    input: {
      clientId: clientId,
      htsCode: selectedHtsCodeForApi,
    }
  }, {
    // Only fetch if we have a client AND an HTS code selected
    skip: !clientId || !selectedHtsCodeForApi,
  });

  // Clear selected country when data changes (e.g. HTS code or year filter change)
  // This prevents stale data from being displayed for a country that might have different or no data in the new result set
  useEffect(() => {
    setSelectedCountry(null);
  }, [data]);

  // Get top 10 country names for duty calculation (sorted by value)
  const top10Countries = useMemo(() => {
    if (!data?.getCensusTradeData?.dataByCountry) return [];
    return [...data.getCensusTradeData.dataByCountry]
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 10)
      .map((c) => c.country);
  }, [data]);

  // Fetch duty rates for top 10 countries when we have a 10-digit HTS code
  const isTenDigitCode = !!selectedHtsCodeForApi && selectedHtsCodeForApi.length === 10;
  
  const { data: dutyRatesData, isFetching: isDutyFetching } = useCalculateDutyRatesForCountriesQuery(
    {
      htsCode: selectedHtsCodeForApi || '',
      countries: top10Countries,
    },
    {
      skip: !isTenDigitCode || top10Countries.length === 0,
    }
  );

  // Lazy query to fetch duty rate for a clicked country (when not in top 10)
  const [fetchClickedCountryDuty, { data: clickedCountryDutyData, isFetching: isClickedDutyFetching }] = 
    useLazyCalculateDutyRatesForCountriesQuery();

  // Create a map of country -> duty rate for quick lookup
  // Combines top 10 rates with any clicked country's rate
  const dutyRateMap = useMemo(() => {
    const map = new Map<string, { totalDutyRate: number; generalDutyRate: number; additionalDutiesRate: number }>();
    // Don't populate with stale data while fetching new rates
    if (isDutyFetching) return map;
    
    // Add top 10 country rates
    if (dutyRatesData?.calculateDutyRatesForCountries?.countryDutyRates) {
      dutyRatesData.calculateDutyRatesForCountries.countryDutyRates.forEach((rate) => {
        map.set(rate.country, {
          totalDutyRate: rate.totalDutyRate,
          generalDutyRate: rate.generalDutyRate,
          additionalDutiesRate: rate.additionalDutiesRate,
        });
      });
    }
    
    // Add clicked country's rate (if fetched, not stale, and not already in map)
    // Only use if the HTS code matches the current selection to prevent stale data
    const clickedDataHtsCode = clickedCountryDutyData?.calculateDutyRatesForCountries?.htsCode;
    const isClickedDataForCurrentHts = clickedDataHtsCode === selectedHtsCodeForApi;
    
    if (clickedCountryDutyData?.calculateDutyRatesForCountries?.countryDutyRates && 
        !isClickedDutyFetching && 
        isClickedDataForCurrentHts) {
      clickedCountryDutyData.calculateDutyRatesForCountries.countryDutyRates.forEach((rate) => {
        if (!map.has(rate.country)) {
          map.set(rate.country, {
            totalDutyRate: rate.totalDutyRate,
            generalDutyRate: rate.generalDutyRate,
            additionalDutiesRate: rate.additionalDutiesRate,
          });
        }
      });
    }
    
    return map;
  }, [dutyRatesData, isDutyFetching, clickedCountryDutyData, isClickedDutyFetching, selectedHtsCodeForApi]);

  const handleClearSearch = useCallback(() => {
    setHtsSearchValue(null);
    setSearchTerm("");
    setDebouncedSearchTerm("");
  }, []);

  const handleSearchModeChange = useCallback((newMode: 'search' | 'your_codes') => {
    setSearchMode(newMode);
    // Clear search when switching modes
    setSearchTerm("");
    setDebouncedSearchTerm("");
  }, []);

  const handleYearChange = (event: SelectChangeEvent<string>) => {
    setSelectedYear(event.target.value);
  };

  // Process Data for Map
  const { countryDataMap, maxCountryPercentage, topCountries, totalImportValue } = useMemo(() => {
    const map = new Map<string, CountryData>();
    let maxPct = 0;
    let totalVal = 0;

    if (data?.getCensusTradeData?.dataByCountry) {
      data.getCensusTradeData.dataByCountry.forEach((item: CountryData) => {
        map.set(item.country, item);
        Object.entries(COUNTRY_NAME_MAP).forEach(([alias, canonical]) => {
          if (item.country === canonical) {
            map.set(alias, item);
          }
        });
        if (typeof item.percentage === 'number') {
          maxPct = Math.max(maxPct, item.percentage);
        }
        totalVal += item.totalValue || 0;
      });
    }

    const sorted = [...(data?.getCensusTradeData?.dataByCountry || [])]
      .sort((a: CountryData, b: CountryData) => b.totalValue - a.totalValue)
      .slice(0, 10) as CountryData[];

    return { 
      countryDataMap: map, 
      maxCountryPercentage: maxPct, 
      topCountries: sorted,
      totalImportValue: totalVal
    };
  }, [data]);

  // Color scale
  const { colorScale, colorScaleMax } = useMemo(() => {
    const effectiveMax = maxCountryPercentage > 0 ? maxCountryPercentage : 5;
    return {
      colorScale: scaleLinear<string>()
        .domain([0, effectiveMax * 0.1, effectiveMax * 0.3, effectiveMax])
        .range(["#E8F4F5", "#7ECFD3", "#2A9D8F", "#0B494B"])
        .clamp(true),
      colorScaleMax: effectiveMax
    };
  }, [maxCountryPercentage]);

  const getCountryFill = useCallback((countryName: string) => {
    const mappedName = COUNTRY_NAME_MAP[countryName] || countryName;
    const countryData = countryDataMap.get(countryName) || countryDataMap.get(mappedName);
    if (!countryData) return "#F1F5F9";
    return colorScale(countryData.percentage);
  }, [countryDataMap, colorScale]);

  const handleCountryClick = useCallback((countryName: string) => {
    const mappedName = COUNTRY_NAME_MAP[countryName] || countryName;
    const countryData = countryDataMap.get(countryName) || countryDataMap.get(mappedName);
    const actualCountryName = countryData?.country || mappedName;
    
    if (countryData) {
      setSelectedCountry(countryData);
    } else {
      setSelectedCountry({
        country: actualCountryName,
        totalValue: 0,
        percentage: 0,
        totalQuantity: null,
        unit: null,
        unitPrice: null
      });
    }
    
    // Fetch duty rate for clicked country if we have a 10-digit code and don't already have the rate
    if (isTenDigitCode && selectedHtsCodeForApi && !dutyRateMap.has(actualCountryName)) {
      fetchClickedCountryDuty({
        htsCode: selectedHtsCodeForApi,
        countries: [actualCountryName],
      });
    }
  }, [countryDataMap, isTenDigitCode, selectedHtsCodeForApi, dutyRateMap, fetchClickedCountryDuty]);

  const handleZoomIn = () => setPosition(pos => ({ ...pos, zoom: Math.min(pos.zoom * 1.5, 8) }));
  const handleZoomOut = () => setPosition(pos => ({ ...pos, zoom: Math.max(pos.zoom / 1.5, 1) }));
  const handleReset = () => setPosition({ coordinates: [10, 20], zoom: 1 });
  const handleMoveEnd = (pos: { coordinates: [number, number], zoom: number }) => setPosition(pos);

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rounded" sx={{ height: 72, mb: 2.5, borderRadius: 3 }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 1.5, md: 3 },
            height: { xs: 'auto', md: 'calc(100vh - 280px)' },
            minHeight: { xs: 0, md: 500 },
          }}
        >
          <Skeleton
            variant="rounded"
            sx={{ flex: 2, height: { xs: 320, md: '100%' }, borderRadius: 3 }}
          />
          <Skeleton
            variant="rounded"
            sx={{ width: { xs: '100%', md: 360 }, height: { xs: 240, md: '100%' }, borderRadius: 3 }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters Bar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, sm: 2 },
          mb: { xs: 1.5, sm: 2.5 },
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          bgcolor: tokens.bg.surface,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
        >
          {/* HTS Code Search with Toggle inside dropdown */}
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 300 }, maxWidth: { xs: '100%', sm: 450 }, width: { xs: '100%', sm: 'auto' } }}>
            <Autocomplete<AnyHtsOption, false, false, true>
              freeSolo
              options={searchMode === 'search' 
                ? (searchResults?.searchHtsCodes || []) as AnyHtsOption[]
                : filteredClientCodes as AnyHtsOption[]
              }
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (isClientHtsOption(option)) return option.formatted_hts_code;
                return option.hts_code || '';
              }}
              filterOptions={(x) => x}
              loading={searchMode === 'search' ? (isSearchLoading || isSearchFetching) : isLoadingClientCodes}
              value={htsSearchValue as AnyHtsOption | string | null}
              inputValue={searchTerm}
              onInputChange={(_, newInputValue, reason) => {
                if (reason !== 'reset') {
                  setSearchTerm(newInputValue);
                }
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  const cleanCode = newValue.replace(/\./g, '');
                  setHtsSearchValue({ hts_code: formatHTSCode(cleanCode) });
                  setSearchTerm('');
                } else if (newValue && isClientHtsOption(newValue)) {
                  setHtsSearchValue({ hts_code: newValue.formatted_hts_code });
                  setSearchTerm('');
                } else {
                  setHtsSearchValue(newValue);
                  setSearchTerm('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={searchMode === 'search' 
                    ? "Search HTS codes (e.g. 841981)..." 
                    : "Filter your imported HTS codes..."
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: tokens.bg.hover,
                      borderRadius: 2,
                      '&:hover': { bgcolor: '#F1F5F9' },
                      '&.Mui-focused': {
                        bgcolor: tokens.bg.surface,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: tokens.brand.primary,
                          borderWidth: 2
                        }
                      }
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens.text.secondary, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {((searchMode === 'search' && (isSearchLoading || isSearchFetching) && searchTerm.length >= 2) ||
                          (searchMode === 'your_codes' && isLoadingClientCodes)) ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxComponent={(listboxProps) => (
                <Box component="ul" {...listboxProps} sx={{ p: 0, m: 0 }}>
                  {/* Sticky Header with Mode Toggle */}
                  <Box sx={{ 
                    position: 'sticky', 
                    top: 0, 
                    bgcolor: tokens.bg.surface,
                    zIndex: 1,
                    borderBottom: '1px solid #E2E8F0',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Button
                      size="small"
                      variant={searchMode === 'search' ? 'contained' : 'outlined'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSearchModeChange('search');
                      }}
                      startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        fontWeight: tokens.fontWeight.semibold,
                        fontSize: tokens.fontSize.md,
                        borderRadius: 1.5,
                        ...(searchMode === 'search' ? {
                          bgcolor: tokens.brand.primary,
                          '&:hover': { bgcolor: tokens.brand.primaryHover }
                        } : {
                          borderColor: '#E2E8F0',
                          color: tokens.text.secondary,
                          '&:hover': { bgcolor: '#F1F5F9', borderColor: '#E2E8F0' }
                        })
                      }}
                    >
                      Search All
                    </Button>
                    <Button
                      size="small"
                      variant={searchMode === 'your_codes' ? 'contained' : 'outlined'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSearchModeChange('your_codes');
                      }}
                      disabled={!clientId}
                      startIcon={<BusinessCenterIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        fontWeight: tokens.fontWeight.semibold,
                        fontSize: tokens.fontSize.md,
                        borderRadius: 1.5,
                        ...(searchMode === 'your_codes' ? {
                          bgcolor: tokens.brand.primary,
                          '&:hover': { bgcolor: tokens.brand.primaryHover }
                        } : {
                          borderColor: '#E2E8F0',
                          color: tokens.text.secondary,
                          '&:hover': { bgcolor: '#F1F5F9', borderColor: '#E2E8F0' }
                        })
                      }}
                    >
                      Your Codes
                    </Button>
                  </Box>
                  {/* Options */}
                  {listboxProps.children}
                </Box>
              )}
              renderOption={(props, option) => {
                const isClientCode = isClientHtsOption(option);
                const htsCode = isClientCode ? option.formatted_hts_code : option.hts_code;
                const rawCode = isClientCode ? option.hts_code : option.hts_code.replace(/\./g, '');
                
                return (
                  <li {...props} key={rawCode}>
                    <Box sx={{ py: 0.5, width: '100%' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography 
                            variant="body2" 
                            fontWeight={tokens.fontWeight.bold}
                            sx={{ 
                              bgcolor: '#E8F4F5', 
                              px: 1, 
                              py: 0.25, 
                              borderRadius: 1,
                              fontFamily: 'monospace'
                            }}
                          >
                            {htsCode}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{
                              color: rawCode.length === 10 ? tokens.brand.primary : tokens.text.secondary,
                              fontWeight: rawCode.length === 10 ? tokens.fontWeight.bold : tokens.fontWeight.normal
                            }}
                          >
                            {rawCode.length === 10 ? '10-digit' : `${rawCode.length}-digit`}
                          </Typography>
                        </Stack>
                        {isClientCode ? (
                          <Typography variant="caption" sx={{ color: tokens.text.secondary, fontWeight: tokens.fontWeight.semibold }}>
                            ${(option.goods_value / 1000000).toFixed(1)}M
                          </Typography>
                        ) : option.description ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: tokens.text.secondary,
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {option.description}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Box>
                  </li>
                );
              }}
              ListboxProps={{
                sx: {
                  maxHeight: 400,
                  p: 0,
                  '& .MuiAutocomplete-option': {
                    borderBottom: '1px solid #F1F5F9',
                    '&:last-child': { borderBottom: 'none' }
                  }
                }
              }}
              noOptionsText={
                searchMode === 'search'
                  ? (searchTerm.length < 2 ? "Type at least 2 characters" : "No HTS codes found")
                  : (sortedClientHtsCodes.length === 0 ? "No HTS codes found for your imports" : "No matching HTS codes")
              }
            />
          </Box>

          {/* Year Dropdown */}
          <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <CalendarIcon sx={{ color: tokens.text.secondary, fontSize: 20, ml: 0.5 }} />
                </InputAdornment>
              }
              sx={{
                bgcolor: tokens.bg.hover,
                borderRadius: 2,
                '&:hover': { bgcolor: '#F1F5F9' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E2E8F0',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: tokens.brand.primary,
                  borderWidth: 2,
                },
              }}
            >
              {YEAR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Active Filter Chip */}
          {selectedHtsCode && (
            <Chip
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" fontWeight={tokens.fontWeight.bold}>
                    {selectedHtsCode}
                  </Typography>
                  {selectedDescription && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: tokens.text.secondary,
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      · {selectedDescription}
                    </Typography>
                  )}
                </Stack>
              }
              onDelete={handleClearSearch}
              deleteIcon={<ClearIcon fontSize="small" />}
              sx={{
                bgcolor: '#E8F4F5',
                color: tokens.brand.primary,
                borderRadius: 2,
                height: 36,
                '& .MuiChip-deleteIcon': {
                  color: tokens.brand.primary,
                  '&:hover': { color: tokens.brand.primaryHover }
                }
              }}
            />
          )}

          {/* Loading indicator */}
          {isFetching && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 'auto' }}>
              <CircularProgress size={18} />
              <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Updating...</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Map and Detail Panel */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1.5, md: 2.5 },
        height: { xs: 'auto', md: 'calc(100vh - 280px)' },
        minHeight: { xs: 0, md: 520 },
        position: 'relative'
      }}>
        {/* Main Map Container */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            border: '1px solid #E2E8F0',
            overflow: 'hidden',
            bgcolor: '#FAFCFD',
            height: { xs: 'min(60vh, 460px)', md: 'auto' },
            minHeight: { xs: 320, md: 0 },
          }}
        >
          {/* Map Header */}
          <Box sx={{
            px: { xs: 1.5, sm: 2.5, md: 3 },
            py: { xs: 1.25, md: 2 },
            borderBottom: '1px solid #E2E8F0',
            bgcolor: tokens.bg.surface,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 1.5 },
          }}>
            <Box>
              <Typography variant="h6" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary} sx={{ fontSize: '1.1rem' }}>
                {selectedHtsCode ? (
                  <>
                    Import Analysis:{' '}
                    <HTSCodeWithTooltip htsCode={selectedHtsCodeForApi || ''}>
                      <span style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}>
                        {selectedHtsCode}
                      </span>
                    </HTSCodeWithTooltip>
                  </>
                ) : 'Global Import Overview'}
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.text.secondary, mt: 0.25 }}>
                {selectedYear === 'all' ? 'All Years (2022-Present)' : selectedYear} • {topCountries.length > 0 ? topCountries.length : 0}+ trading partners
              </Typography>
            </Box>
            {clientId && averageUnitCostData?.data && averageUnitCostData.data.recordCount > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ 
                  px: 2, 
                  py: 0.75, 
                  bgcolor: '#E8F4F5', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <InventoryIcon sx={{ fontSize: 18, color: tokens.brand.primary }} />
                  <Typography variant="body2" fontWeight={tokens.fontWeight.bold} color={tokens.brand.primary} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span>
                      Avg Unit Cost: $
                      {new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(averageUnitCostData.data.averageUnitCost)}
                    </span>
                    <Tooltip
                      placement="bottom"
                      arrow
                      title={
                        <Box sx={{ maxWidth: 440, p: 2 }}>
                          <Typography variant="body2" color="inherit" sx={{ fontWeight: tokens.fontWeight.heavy, mb: 1 }}>
                            Avg Unit Cost Assumptions
                          </Typography>
                          <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.0 } }}>
                            <li>
                              <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.5 }}>
                                Calculated from historical ACE data for this importer and the selected HTS code.
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.5 }}>
                                Quantity basis: Line Tariff Quantity (1) where Quantity &gt; 0.
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.5 }}>
                                Calculated with historical data across Origins:{' '}
                                {(averageUnitCostData.data.origins || []).length > 0
                                  ? (averageUnitCostData.data.origins || []).join(', ')
                                  : 'N/A'}
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.5 }}>
                                Calculated for dates:{' '}
                                {averageUnitCostData.data.dateRange?.minDate
                                  ? new Date(averageUnitCostData.data.dateRange.minDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                  : 'N/A'}{' '}
                                →{' '}
                                {averageUnitCostData.data.dateRange?.maxDate
                                  ? new Date(averageUnitCostData.data.dateRange.maxDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                  : 'Present'}
                              </Typography>
                            </li>
                          </Box>
                        </Box>
                      }
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: { offset: [0, -8] },
                            },
                          ],
                        },
                        tooltip: {
                          sx: {
                            bgcolor: '#0F1E2E',
                            color: 'white',
                            fontSize: tokens.fontSize.lg,
                            borderRadius: tokens.radius.lg,
                            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
                            '& .MuiTooltip-arrow': {
                              color: '#0F1E2E',
                            },
                          },
                        },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: tokens.brand.primary, opacity: 0.9 }} />
                    </Tooltip>
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Map Area */}
          <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 140, center: [0, 30] }}
              style={{ width: '100%', height: '100%' }}
            >
              <ZoomableGroup
                zoom={position.zoom}
                center={position.coordinates}
                onMoveEnd={handleMoveEnd}
                maxZoom={8}
                minZoom={1}
              >
                <Geographies geography={GEO_URL}>
                  {({ geographies }: { geographies: any[] }) =>
                    geographies.map((geo) => {
                      const countryName = geo.properties.name;
                      const isHovered = hoveredCountry === countryName;
                      const isSelected = selectedCountry?.country === countryName || 
                        selectedCountry?.country === (COUNTRY_NAME_MAP[countryName] || countryName);

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={isSelected ? tokens.brand.primary : getCountryFill(countryName)}
                          stroke={isSelected ? "#FFD700" : isHovered ? tokens.brand.primary : "#CBD5E1"}
                          strokeWidth={isSelected ? 2.5 : isHovered ? 1.2 : 0.4}
                          onClick={() => handleCountryClick(countryName)}
                          onMouseEnter={() => setHoveredCountry(countryName)}
                          onMouseLeave={() => setHoveredCountry(null)}
                          style={{
                            default: { 
                              outline: 'none',
                              transition: 'all 0.2s ease',
                              filter: isSelected ? 'drop-shadow(0 0 8px rgba(11, 73, 75, 0.5))' : 'none',
                            },
                            hover: { 
                              outline: 'none',
                              cursor: 'pointer',
                              filter: isSelected ? 'drop-shadow(0 0 8px rgba(11, 73, 75, 0.5))' : 'brightness(0.92)',
                            },
                            pressed: { 
                              outline: 'none',
                              filter: 'brightness(0.85)',
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            {/* Zoom Controls */}
            <Stack 
              sx={{ 
                position: 'absolute', 
                bottom: 16, 
                right: 16,
                bgcolor: tokens.bg.surface,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                overflow: 'hidden'
              }}
            >
              <IconButton size="small" onClick={handleZoomIn} sx={{ borderRadius: 0, p: 1 }}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <Divider />
              <IconButton size="small" onClick={handleZoomOut} sx={{ borderRadius: 0, p: 1 }}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <Divider />
              <IconButton size="small" onClick={handleReset} sx={{ borderRadius: 0, p: 1 }}>
                <ResetIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* Legend */}
            <Box sx={{ 
              position: 'absolute', 
              bottom: 16, 
              left: 16,
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 2,
              p: 1.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              minWidth: 140
            }}>
              <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.excerpt} sx={{ mb: 1, display: 'block' }}>
                Import Share
              </Typography>
              <Box sx={{ 
                height: 10, 
                borderRadius: 1,
                background: 'linear-gradient(to right, #E8F4F5, #7ECFD3, #2A9D8F, #0B494B)',
                mb: 0.5
              }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: tokens.text.secondary }}>0%</Typography>
                <Typography variant="caption" sx={{ color: tokens.text.secondary }}>{colorScaleMax.toFixed(1)}%</Typography>
              </Stack>
            </Box>

            {/* Hovered Country Tooltip */}
            {hoveredCountry && !selectedCountry && (
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                bgcolor: tokens.bg.surface,
                borderRadius: 2,
                p: 1.5,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: 180,
                pointerEvents: 'none'
              }}>
                <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold}>{hoveredCountry}</Typography>
                {(() => {
                  const mappedName = COUNTRY_NAME_MAP[hoveredCountry] || hoveredCountry;
                  const cd = countryDataMap.get(hoveredCountry) || countryDataMap.get(mappedName);
                  if (!cd) return <Typography variant="caption" sx={{ color: tokens.text.secondary }}>No import data</Typography>;
                  return (
                    <>
                      <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.brand.primary }}>
                        {cd.percentage.toFixed(2)}% of imports
                      </Typography>
                      <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                        ${numberWithCommas(Math.round(cd.totalValue))}
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            )}

            {/* Country Detail Popup (Floating Overlay - inside map area) */}
            {(() => {
              // Position popup on opposite side of the country
              const countryLng = selectedCountry ? getCountryLongitude(selectedCountry.country) : 0;
              const popupOnRight = countryLng < 0; // Americas = popup on right, Asia/Europe = popup on left
              
              return (
              <Fade in={!!selectedCountry} timeout={200}>
                <Paper
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    // On mobile, the popup anchors to the bottom of the map
                    // so the user can still see the country they tapped.
                    // On md+, it floats opposite the country (left/right).
                    left: { xs: 8, md: popupOnRight ? 'auto' : 12 },
                    right: { xs: 8, md: popupOnRight ? 12 : 'auto' },
                    top: { xs: 'auto', md: 12 },
                    bottom: { xs: 8, md: 12 },
                    width: { xs: 'auto', md: 260 },
                    maxHeight: { xs: '65%', md: 'none' },
                    display: selectedCountry ? 'flex' : 'none',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid #E2E8F0',
                    overflow: 'hidden',
                    zIndex: 10,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  }}
                >
            {selectedCountry && (
              <>
                {/* Panel Header */}
                <Box sx={{ 
                  px: 1.5, 
                  py: 1, 
                  bgcolor: tokens.brand.primary,
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minHeight: 36,
                }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <PublicIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                    <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ lineHeight: 1.2, color: 'white' }}>
                      {selectedCountry.country}
                    </Typography>
                  </Stack>
                  <IconButton 
                    size="small" 
                    onClick={() => setSelectedCountry(null)}
                    sx={{ color: 'white', p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                {/* Panel Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                  {selectedCountry.totalValue === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <PublicIcon sx={{ fontSize: 36, color: '#CBD5E1', mb: 1.5 }} />
                      <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                        No import data recorded for this country
                        {selectedHtsCode ? ` under HTS ${selectedHtsCode}` : ''}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Primary Stat - Unit Price for 10-digit codes, otherwise Percentage */}
                      <PrimaryStatBox 
                        country={selectedCountry} 
                        isUnitPrice={isTenDigitCode && selectedCountry.unitPrice != null && selectedCountry.unitPrice > 0} 
                      />

                      {/* Stats Grid */}
                      <Stack spacing={1.5}>
                        {/* Show percentage as secondary stat for 10-digit codes */}
                        {isTenDigitCode && selectedCountry.unitPrice != null && (
                          <>
                            <StatRow 
                              icon={<TrendingUpIcon />}
                              label="Import Share"
                              value={`${selectedCountry.percentage.toFixed(2)}%`}
                              subValue="of total imports"
                            />
                            <Divider />
                          </>
                        )}
                        
                        <StatRow 
                          icon={<TrendingUpIcon />}
                          label="Total Import Value"
                          value={`$${numberWithCommas(Math.round(selectedCountry.totalValue))}`}
                          subValue={`of $${numberWithCommas(Math.round(totalImportValue))} total`}
                        />
                        
                        {isTenDigitCode && selectedCountry.totalQuantity != null && (
                          <>
                            <Divider />
                            <StatRow 
                              icon={<InventoryIcon />}
                              label="Quantity Imported"
                              value={`${numberWithCommas(Math.round(selectedCountry.totalQuantity))}`}
                              subValue={selectedCountry.unit || 'units'}
                            />
                          </>
                        )}
                      </Stack>

                      {/* Duty Rate Section */}
                      {isTenDigitCode && dutyRateMap.has(selectedCountry.country) && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 1.5, 
                          bgcolor: '#FEF2F2', 
                          borderRadius: 1.5,
                          border: '1px solid #FECACA'
                        }}>
                          <Typography variant="caption" sx={{ color: '#991B1B', letterSpacing: 0.5, display: 'block', mb: 0.5, fontWeight: tokens.fontWeight.semibold, textTransform: 'uppercase', fontSize: tokens.fontSize["2xs"] }}>
                            Current Duty Rate
                          </Typography>
                          <Typography variant="h6" fontWeight={tokens.fontWeight.heavy} color={tokens.status.danger} sx={{ lineHeight: 1.2 }}>
                            {(dutyRateMap.get(selectedCountry.country)!.totalDutyRate * 100).toFixed(2)}%
                          </Typography>
                          <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize["2xs"] }}>General (MFN)</Typography>
                              <Typography variant="caption" fontWeight={tokens.fontWeight.bold} sx={{ display: 'block' }}>
                                {(dutyRateMap.get(selectedCountry.country)!.generalDutyRate * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                            {dutyRateMap.get(selectedCountry.country)!.additionalDutiesRate > 0 && (
                              <Box>
                                <Typography variant="caption" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize["2xs"] }}>Additional Tariffs</Typography>
                                <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.status.danger} sx={{ display: 'block' }}>
                                  +{(dutyRateMap.get(selectedCountry.country)!.additionalDutiesRate * 100).toFixed(2)}%
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      )}

                      {/* Duty Rate Loading State */}
                      {isTenDigitCode && !dutyRateMap.has(selectedCountry.country) && (isDutyFetching || isClickedDutyFetching) && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 1.5, 
                          bgcolor: '#FEF2F2', 
                          borderRadius: 1.5,
                          border: '1px solid #FECACA'
                        }}>
                          <Typography variant="caption" sx={{ color: '#991B1B', letterSpacing: 0.5, display: 'block', mb: 0.5, fontWeight: tokens.fontWeight.semibold, textTransform: 'uppercase', fontSize: tokens.fontSize["2xs"] }}>
                            Current Duty Rate
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <CircularProgress size={14} sx={{ color: tokens.status.danger }} />
                            <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                              Calculating...
                            </Typography>
                          </Stack>
                        </Box>
                      )}

                      {/* Total Landed Cost Section */}
                      {isTenDigitCode && selectedCountry.unitPrice != null && selectedCountry.unitPrice > 0 && dutyRateMap.has(selectedCountry.country) && (
                        <Box sx={{ 
                          mt: 1.5,
                          p: 1.5,
                          bgcolor: tokens.bg.hover,
                          borderRadius: 1.5,
                          border: '1px solid #E2E8F0'
                        }}>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary, letterSpacing: 0.5, display: 'block', mb: 0.5, fontWeight: tokens.fontWeight.semibold, textTransform: 'uppercase', fontSize: tokens.fontSize["2xs"] }}>
                            Total Landed Cost
                          </Typography>
                          <Typography variant="h6" fontWeight={tokens.fontWeight.heavy} color={tokens.text.primary} sx={{ lineHeight: 1.2 }}>
                            ${(selectedCountry.unitPrice * (1 + dutyRateMap.get(selectedCountry.country)!.totalDutyRate)).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: selectedCountry.unitPrice < 1 ? 4 : 2
                            })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary, display: 'block', mt: 0.25, fontSize: tokens.fontSize["2xs"] }}>
                            per {selectedCountry.unit || 'unit'} (incl. {(dutyRateMap.get(selectedCountry.country)!.totalDutyRate * 100).toFixed(0)}% duty)
                          </Typography>
                          <Stack direction="row" spacing={1.5} sx={{ mt: 1, pt: 1, borderTop: '1px solid #E2E8F0' }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize["2xs"] }}>Unit Cost</Typography>
                              <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary} sx={{ display: 'block' }}>
                                ${selectedCountry.unitPrice.toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: selectedCountry.unitPrice < 1 ? 4 : 2
                                })}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize["2xs"] }}>Duty Cost</Typography>
                              <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.status.danger} sx={{ display: 'block' }}>
                                +${(selectedCountry.unitPrice * dutyRateMap.get(selectedCountry.country)!.totalDutyRate).toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: selectedCountry.unitPrice < 1 ? 4 : 2
                                })}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      )}

                      {/* Landed Cost Loading State */}
                      {isTenDigitCode && selectedCountry.unitPrice != null && selectedCountry.unitPrice > 0 && !dutyRateMap.has(selectedCountry.country) && (isDutyFetching || isClickedDutyFetching) && (
                        <Box sx={{ 
                          mt: 1.5,
                          p: 1.5,
                          bgcolor: tokens.bg.hover,
                          borderRadius: 1.5,
                          border: '1px solid #E2E8F0'
                        }}>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary, letterSpacing: 0.5, display: 'block', mb: 0.5, fontWeight: tokens.fontWeight.semibold, textTransform: 'uppercase', fontSize: tokens.fontSize["2xs"] }}>
                            Total Landed Cost
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <CircularProgress size={14} sx={{ color: tokens.brand.primary }} />
                            <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                              Calculating...
                            </Typography>
                          </Stack>
                        </Box>
                      )}

                      {/* Rank Info */}
                      {(() => {
                        const rank = topCountries.findIndex(c => c.country === selectedCountry.country) + 1;
                        if (rank > 0 && rank <= 10) {
                          return (
                            <Box sx={{ 
                              mt: 2, 
                              p: 1.25, 
                              bgcolor: tokens.status.warningBg, 
                              borderRadius: 1.5,
                              border: '1px solid #FCD34D'
                            }}>
                              <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.chip.amber.text} sx={{ display: 'block', lineHeight: 1.3 }}>
                                #{rank} Top Trading Partner
                              </Typography>
                              <Typography variant="caption" color="#B45309" sx={{ fontSize: tokens.fontSize["2xs"] }}>
                                {selectedHtsCode ? `For HTS ${selectedHtsCode}` : 'By total import value'}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                </Box>
              </>
            )}
              </Paper>
            </Fade>
            );
          })()}
          </Box>
        </Paper>

        {/* Top Trading Partners Panel (Always Visible) */}
        {topCountries.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              width: { xs: '100%', md: isTenDigitCode ? 380 : 320 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              overflow: 'hidden',
              maxHeight: { xs: 480, md: 'none' },
            }}
          >
            <Box sx={{ px: { xs: 1.75, sm: 2.5, md: 3 }, py: { xs: 1.25, md: 2 }, borderBottom: '1px solid #E2E8F0', bgcolor: tokens.bg.surface }}>
              <Typography variant="subtitle1" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary}>
                Top Trading Partners
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                {selectedCountry ? `Viewing: ${selectedCountry.country}` : 'Click a country on the map for details'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.25, sm: 2 } }}>
              {/* Column headers */}
              {isTenDigitCode && (dutyRateMap.size > 0 || isDutyFetching) && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  px: 1.5,
                  pb: 1,
                  mb: 1,
                  borderBottom: '1px solid #E2E8F0'
                }}>
                  <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.secondary}>
                    COUNTRY
                  </Typography>
                  {isDutyFetching ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CircularProgress size={12} sx={{ color: tokens.text.secondary }} />
                      <Typography variant="caption" color={tokens.text.secondary}>
                        Loading rates...
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="caption" fontWeight={tokens.fontWeight.bold} color={tokens.text.secondary}>
                      LANDED COST
                    </Typography>
                  )}
                </Box>
              )}
              <Stack spacing={0.5}>
                {topCountries.map((country, idx) => {
                  const isSelected = selectedCountry?.country === country.country;
                  return (
                  <Box 
                    key={country.country}
                    onClick={() => setSelectedCountry(isSelected ? null : country)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: isSelected ? '#E8F4F5' : 'transparent',
                      border: isSelected ? `1px solid ${tokens.brand.primary}` : '1px solid transparent',
                      '&:hover': { bgcolor: isSelected ? '#E8F4F5' : '#F1F5F9' },
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%', 
                          bgcolor: idx < 3 ? tokens.brand.primary : '#E2E8F0',
                          color: idx < 3 ? 'white' : tokens.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: tokens.fontWeight.bold,
                          flexShrink: 0
                        }}
                      >
                        {idx + 1}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} sx={{ 
                          maxWidth: isTenDigitCode ? 120 : 140,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {country.country}
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                          ${numberWithCommas(Math.round(country.totalValue))}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      {isTenDigitCode && country.unitPrice != null && country.unitPrice > 0 && dutyRateMap.has(country.country) ? (
                        <>
                          <Typography variant="body2" fontWeight={tokens.fontWeight.heavy} color={tokens.text.primary}>
                            ${(country.unitPrice * (1 + dutyRateMap.get(country.country)!.totalDutyRate)).toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: country.unitPrice < 1 ? 4 : 2 
                            })}/{country.unit || 'unit'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                            ${country.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + {(dutyRateMap.get(country.country)!.totalDutyRate * 100).toFixed(0)}%
                          </Typography>
                        </>
                      ) : isTenDigitCode && country.unitPrice != null && country.unitPrice > 0 ? (
                        <>
                          <Typography variant="body2" fontWeight={tokens.fontWeight.heavy} color={tokens.text.primary}>
                            ${country.unitPrice.toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: country.unitPrice < 1 ? 4 : 2 
                            })}/{country.unit || 'unit'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                            {country.percentage.toFixed(1)}% share
                          </Typography>
                        </>
                      ) : (
                        <Box sx={{ 
                          bgcolor: '#E8F4F5', 
                          px: 1.5, 
                          py: 0.5, 
                          borderRadius: 1.5 
                        }}>
                          <Typography variant="body2" fontWeight={tokens.fontWeight.bold} color={tokens.brand.primary}>
                            {country.percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  );
                })}
              </Stack>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

// Helper component for stat rows
const StatRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
}> = ({ icon, label, value, subValue }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Box sx={{ 
      p: 0.5, 
      bgcolor: '#E8F4F5', 
      borderRadius: 1,
      color: tokens.brand.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .MuiSvgIcon-root': { fontSize: 14 }
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: tokens.text.secondary, display: 'block', fontSize: tokens.fontSize["2xs"], lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={tokens.fontWeight.bold} color={tokens.text.primary} sx={{ lineHeight: 1.3 }}>
        {value}
      </Typography>
      {subValue && (
        <Typography variant="caption" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize["2xs"] }}>
          {subValue}
        </Typography>
      )}
    </Box>
  </Stack>
);

interface PrimaryStatBoxProps {
  country: CountryData;
  isUnitPrice: boolean;
}

const PrimaryStatBox: React.FC<PrimaryStatBoxProps> = ({ country, isUnitPrice }) => {
  if (isUnitPrice) {
    return (
      <Box sx={{ 
        p: 1.5, 
        bgcolor: tokens.brand.primary, 
        borderRadius: 1.5,
        mb: 1.5,
        textAlign: 'center'
      }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 0.5, fontSize: tokens.fontSize["2xs"], textTransform: 'uppercase' }}>
          Average Unit Cost
        </Typography>
        <Typography variant="h5" fontWeight={tokens.fontWeight.heavy} color="white" sx={{ my: 0.25, lineHeight: 1.2 }}>
          ${country.unitPrice!.toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: country.unitPrice! < 1 ? 4 : 2 
          })}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: tokens.fontSize["2xs"] }}>
          per {country.unit || 'unit'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 1.5,
      bgcolor: tokens.bg.hover,
      borderRadius: 1.5,
      mb: 1.5,
      textAlign: 'center'
    }}>
      <Typography variant="h5" fontWeight={tokens.fontWeight.heavy} color={tokens.brand.primary} sx={{ mb: 0.25, lineHeight: 1.2 }}>
        {country.percentage.toFixed(2)}%
      </Typography>
      <Typography variant="caption" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize["2xs"] }}>
        Share of Total Imports
      </Typography>
    </Box>
  );
};

export default CensusDataView;
